import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, ArrowLeft } from "@phosphor-icons/react";
import { devSyncAutopay, getAutopayStatus } from "@/lib/billing";

/**
 * Stripe Setup-mode Checkout return page.
 *
 * Flow:
 *  1. Try the backend's GET /api/billing/autopay/status/:sessionId — it
 *     asks Stripe + falls back to our DB state.
 *  2. If that still reports pending/unknown (expected on the shared
 *     `sk_test_emergent` proxy because `Session.retrieve` is unsupported),
 *     call POST /api/billing/autopay/sync-dev which marks the session
 *     active using the parent's own JWT. In production with a real Stripe
 *     key, step 1 + the Stripe webhook handle everything automatically.
 */
const MAX_POLLS = 4;
const POLL_MS = 1500;

export default function ParentAutopaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | active | failed | pending
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let polls = 0;

    async function check() {
      try {
        const res = await getAutopayStatus(sessionId);
        if (cancelled) return;
        setDetails(res);
        if (res.status === "active") {
          setState("active");
          return;
        }
        if (res.status === "setup_failed") {
          setState("failed");
          return;
        }
        polls += 1;
        if (polls >= MAX_POLLS) {
          // Dev / emergent-proxy fallback: confirm via our authenticated endpoint.
          try {
            const synced = await devSyncAutopay(sessionId);
            if (cancelled) return;
            setDetails(synced);
            if (synced.status === "active") {
              setState("active");
              return;
            }
          } catch {
            // If dev sync is disabled (prod Stripe), leave as pending and
            // let the webhook reconcile in the background.
          }
          setState("pending");
          return;
        }
        setTimeout(check, POLL_MS);
      } catch (err) {
        if (!cancelled) {
          setState("failed");
          setDetails({ error: err.message });
        }
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      data-testid="parent-autopay-success"
      data-state={state}
      className="space-y-5"
    >
      <Link
        to="/parent/billing"
        data-testid="autopay-success-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <ArrowLeft size={16} weight="bold" />
        Back to billing
      </Link>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Autopay
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">
          {state === "checking" && "Finishing autopay setup…"}
          {state === "active" && "Autopay is on"}
          {state === "pending" && "Autopay still processing"}
          {state === "failed" && "Couldn’t confirm autopay"}
        </h1>
      </header>

      <section
        className={`rounded-2xl border p-6 ${
          state === "active"
            ? "border-primary/30 bg-primary/5"
            : state === "failed"
              ? "border-[#C6851E]/40 bg-[#C6851E]/5"
              : "border-secondary/50 bg-secondary/20"
        }`}
      >
        <div className="flex items-start gap-4">
          {state === "active" ? (
            <CheckCircle size={28} weight="duotone" className="mt-0.5 text-primary-hover" />
          ) : state === "failed" ? (
            <XCircle size={28} weight="duotone" className="mt-0.5 text-[#C6851E]" />
          ) : (
            <div className="mt-1 h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          )}
          <div className="min-w-0 text-sm leading-relaxed text-neutral-800">
            {state === "active" && (
              <p>
                Your payment method is saved. Future tuition invoices will be
                charged automatically on their due date — you can turn this off
                at any time from the billing page.
              </p>
            )}
            {state === "pending" && (
              <p>
                Stripe is still confirming your card. You can safely close this
                page; your autopay status will update automatically within a
                few minutes.
              </p>
            )}
            {state === "failed" && (
              <p>
                We couldn't confirm the autopay setup. Please head back to the
                billing page and try again.
                {details?.failure_reason ? (
                  <span className="mt-2 block text-xs text-neutral-500">
                    Reason: {details.failure_reason}
                  </span>
                ) : null}
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
